import {
    ApiFilterPrefix,
    CustomerFe,
    FilteredQuery,
    GiftcardsFe,
    KeysOfUnion,
    OperatorType,
    QueryPagination,
    SalesFe,
    CashMovementsFe,
    SuppliersFe,
    TableRow,
    StaticTokenFe,
    FileImporterFe,
    FileExporterFe,
    PromotionsFe,
    SaleItemsDeletedFe,
    ItemFe,
} from 'src/app/shared/model/model';
import {BaseModel, ColumnsDefaultModel} from './columns-default/columns-default.model';

import {
    ISimpleFilterModel,
    SetFilterModel,
    DateFilterModel,
    NumberFilterModel,
    TextFilterModel,
    IMultiFilterModel
} from 'ag-grid-community';

import { CustomUIFilter } from '../../../model/grid-custom-filter.model';
import { TilbyDatePipe } from '@tilby/tilby-ui-lib/pipes/tilby-date';
import { Feature } from 'src/app/models';
import { DevLogger } from 'src/app/shared/dev-logger';
import { CategoriesFe } from 'src/app/features/items/components/items-module/categories-tab/categories.models';
import { ComponentsFe } from 'src/app/features/items/components/items-module/components-tab/components.models';
import { RawMaterialsFe } from 'src/app/features/items/components/items-module/raw-materials-tab/raw-materials.model';
import { DepartmentsFe } from 'src/app/features/items/components/items-module/departments-tab/departments.model';
import { VatRatesFe } from 'src/app/features/items/components/items-module/vat-rates-tab/vat-rates-model';
import { SalesDataSentFe } from 'src/app/features/history/history-sales-data-sent/history-sales-data-sent.model';
import { SalesEInvoicesFe } from 'src/app/features/history/history-e-invoices/history-e-invoices.model';
import { StockFe } from 'src/app/features/stock/stock-management/model/stock-model';
import { StockMovementSearchFe, StockMovementsFe } from 'src/app/features/stock/stock-movements/models/stock-movements.model';

type XQueryFilterOrder = ((QueryPagination | FilteredQuery) & { [prop in KeysOfUnion<TableRow>]?: any & TableRow } & { x_query?: string });

const GridStateDefault = new Map(Object.keys(ColumnsDefaultModel).flatMap((feature) => [
    // [`grid.${feature}.tablet.landscape.columns`, ColumnsDefaultModel[feature].tablet_landscape_columns],
    // [`grid.${feature}.tablet.portrait.columns`, ColumnsDefaultModel[feature].tablet_portrait_columns],
    // [`grid.${feature}.mobile.landscape.columns`, ColumnsDefaultModel[feature].mobile_landscape_columns],
    // [`grid.${feature}.mobile.portrait.columns`, ColumnsDefaultModel[feature].mobile_portrait_columns],
    [`grid.${feature}.tablet.columns`, (<BaseModel>(ColumnsDefaultModel[feature as keyof ColumnsDefaultModel])).tablet_columns],
    [`grid.${feature}.mobile.columns`, (<BaseModel>(ColumnsDefaultModel[feature as keyof ColumnsDefaultModel])).mobile_columns]
]));

export default class GridUtils {

    private getFieldsByFeature(feature: Feature): string[] | undefined {
        switch (feature) {
            case 'customers':
                return Object.getOwnPropertyNames(new CustomerFe());
            case 'suppliers':
                return Object.getOwnPropertyNames(new SuppliersFe());
            case 'history_sales':
                return Object.getOwnPropertyNames(new SalesFe());
            case 'history_sales_data_sent':
                return this.getAllKeys(new SalesDataSentFe());
            case 'history_e_invoices':
                return this.getAllKeys(new SalesEInvoicesFe());
            case 'giftcards':
                return Object.getOwnPropertyNames(new GiftcardsFe());
            case 'settings_um_static_token':
                return Object.getOwnPropertyNames(new StaticTokenFe());
            case 'history_cash_movements':
                return Object.getOwnPropertyNames(new CashMovementsFe());
            case 'file_importer':
                return Object.getOwnPropertyNames(new FileImporterFe())
            case 'file_exporter':
                return Object.getOwnPropertyNames(new FileExporterFe())
            case 'promotions':
                return Object.getOwnPropertyNames(new PromotionsFe());
            case 'history_products_deleted':
                return Object.getOwnPropertyNames(new SaleItemsDeletedFe());
            case 'items':
                return Object.getOwnPropertyNames(new ItemFe());
            case 'categories':
                return Object.getOwnPropertyNames(new CategoriesFe());
            case 'components':
                return Object.getOwnPropertyNames(new ComponentsFe());
            case 'raw_materials':
                return Object.getOwnPropertyNames(new RawMaterialsFe());
            case 'departments':
                return Object.getOwnPropertyNames(new DepartmentsFe());
            case 'vat_rates':
                return Object.getOwnPropertyNames(new VatRatesFe());
            case 'stock':
                return Object.getOwnPropertyNames(new StockFe());
            case 'stock_movements':
                return Object.getOwnPropertyNames(new StockMovementsFe());
            case 'stock_movements_search':
                return Object.getOwnPropertyNames(new StockMovementSearchFe());
        }
    }

    private getAllKeys(obj: any, prefix = ''): string[] {
        let keys: string[] = [];
        for (const key in obj) {
            const value = obj[key];
            const newKey = prefix ? `${prefix}.${key}` : key;

            if (Array.isArray(value)) {
                if (value.length > 0) {
                    keys = keys.concat(this.getAllKeys(value[0], `${newKey}.0`));
                }
            } else if (value !== null && typeof value === 'object' && !(value instanceof Date)) {
                keys = keys.concat(this.getAllKeys(value, newKey));
            } else {
                keys.push(newKey);
            }
        }
        return keys;
    }

    getConfiguration(type: string, feature: Feature, index = -1): string {
        DevLogger.debug('[ GridUtils.getConfiguration ]', type, feature, index, GridStateDefault, GridStateDefault.get(type));
        const config = GridStateDefault.get(type);
        if (!config) return JSON.stringify([]);

        let configurationParse = JSON.parse(JSON.stringify(config));

        let fields = this.getFieldsByFeature(feature);
        fields?.forEach((element) => {
            let existingField = configurationParse.find((c: any) => c.colId === element);
            if (existingField) {
                index = configurationParse.indexOf(existingField);
            } else {
                configurationParse.splice(++index, 0, { 'colId': element, 'hide': true });
            }
        });
        return JSON.stringify(configurationParse);
    }
}

export const filterType = (k: string, v: any, formatter: any) => {
    const filterType = typeof v === 'object'
        ? v instanceof Date
            ? 'date'
            : v?.type
        : typeof v === 'boolean'
            ? 'set'
            : typeof v === 'string'
                ? 'text'
                : 'number'

    const commonFilterParams = ['equals', 'blank', 'notBlank'] as Partial<ISimpleFilterModel>[];
    // console.log("FILTER TYPE per ", k, v, formatter)
    // console.log("FILTER TYPE ---------> ", filterType)
    return {
        filter: `ag${filterType?.toTitleCase()}ColumnFilter`,
        filterParams: {
            valueFormatter: formatter,
            ...(filterType === 'text' && {filterOptions: ['contains', ...commonFilterParams] as Partial<ISimpleFilterModel>[]}),
            ...((filterType === 'number' || filterType === 'date') && {filterOptions: [...commonFilterParams, 'inRange', 'lessThanOrEqual', 'greaterThanOrEqual'] as Partial<ISimpleFilterModel>[]}),
            // ...(filterType === 'set' && {values: typeof v === 'object'
            //         ? (params: any) => v.getUIFilter(params)
            //         : (params: any) => params.success([true, false])
            // }),

            ...(filterType === 'set' && typeof v === 'boolean' && { 
                values: (params: any) => params.success([true, false]) 
            }),
            ...(filterType === 'set' && typeof v === 'object' && v.getUIFilter()),
            ...(filterType === 'multi' && {filters: v.getUIFilter()}),
            
            comparator:  filterType === 'date' ? (filterLocalDateAtMidnight: Date, cellValue: Date) => {
                if(!cellValue) return -1;

                const dateAsString =  convertDate(cellValue);

                if (dateAsString == null) return -1;
                    const dateParts = dateAsString.split("/");
                    const cellDate = new Date(
                    Number(dateParts[2]),
                    Number(dateParts[1]) - 1,
                    Number(dateParts[0])
                );
                if (filterLocalDateAtMidnight.getTime() === cellDate.getTime()) {
                    return 0;
                }
                if (cellDate < filterLocalDateAtMidnight) {
                    return -1;
                }
                if (cellDate > filterLocalDateAtMidnight) {
                    return 1;
                }
    
                return -1;
            } : undefined
        },
        // ...(filterType==='set' && typeof v === 'object' && {valueGetter: params => v.__proto__.getUIFilterValue(params.data[k])||params.data[k]}),
    }
}

const convertDate = (date: Date) => {
    const d = typeof date === 'string' ? new Date(date) : date;

    if (!d || isNaN(d.getTime())) {
        console.error("Valore data non valido ricevuto:", date);
        return null;
    }

    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();

    return `${day}/${month}/${year}`;
}

export const queryFilteredOrdered_FEtoBEConverter = (queryFilteredOrdered: XQueryFilterOrder, 
    columnName: KeysOfUnion<TableRow>, 
    f: TextFilterModel | NumberFilterModel | DateFilterModel | SetFilterModel | IMultiFilterModel, 
    dataType: any & TableRow) => 
{
    console.log("queryFilteredOrdered_FEtoBEConverter ", queryFilteredOrdered, columnName, f, dataType);
    const null_notnull = {
        ...((<TextFilterModel | NumberFilterModel | DateFilterModel>f).type === 'blank' && {[`${columnName}`]: 'null'}),   // null
        ...((<TextFilterModel | NumberFilterModel | DateFilterModel>f).type === 'notBlank' && {[`${columnName}`]: 'notnull'}),   // notnull
    };
    if (f.filterType === 'text' && (typeof dataType[columnName] === 'string'))
        queryFilteredOrdered = {
            ...queryFilteredOrdered,
            ...(f.filter && f.type === 'contains' && {[`${columnName}_like`]: f.filter}),   // partial=
            ...(f.filter && f.type === 'equals' && {[`${columnName}`]: f.filter}),   // =
            ...null_notnull
        };
    else if (f.filterType === 'number' && (typeof dataType[columnName] === 'number'))
        queryFilteredOrdered = {
            ...queryFilteredOrdered,
            ...(f.filter && f.type === 'equals' && {[`${columnName}`]: f.filter}),   // =
            ...null_notnull,
            ...(f.filter && (f.type === 'greaterThanOrEqual' || f.type === 'inRange') && {[`${columnName}_since`]: f.filter}),   // >=
            ...(f.filterTo && (f.type === 'inRange') && {[`${columnName}_max`]: f.filterTo}), // <=
            ...(f.filter && (f.type === 'lessThanOrEqual') && {[`${columnName}_max`]: f.filter})  // <=
        };
    else if (f.filterType === 'date' && (typeof dataType[columnName] === 'object'))
        queryFilteredOrdered = {
            ...queryFilteredOrdered,
            ...(f.dateFrom && f.type === 'equals' && {[`${columnName}_since`]: TilbyDatePipe.shopDateGridFilterFrom(f.dateFrom), [`${columnName}_max`]: TilbyDatePipe.shopDateGridFilterTo(f.dateFrom)}),   // =
            ...null_notnull,
            ...(f.dateFrom && (f.type === 'greaterThanOrEqual' || f.type === 'inRange') && {[`${columnName}_since`]: TilbyDatePipe.shopDateGridFilterFrom(f.dateFrom)}),   // >=
            ...(f.dateFrom && (f.type === 'lessThanOrEqual') && {[`${columnName}_max`]: TilbyDatePipe.shopDateGridFilterTo(f.dateFrom)}),  // <=
            ...(f.dateTo && (f.type === 'inRange') && {[`${columnName}_max`]: TilbyDatePipe.shopDateGridFilterTo(f.dateTo)})  // <=
        };
    else if (f.filterType === 'set' && (typeof dataType[columnName] === 'boolean')) {
        queryFilteredOrdered[`${columnName}`] = f.values;
    } 

    else if (dataType[columnName]?.getConversionFilterObject) {
        const flatFilter = dataType[columnName].getConversionFilterObject(f);
        queryFilteredOrdered = {
            ...queryFilteredOrdered,
            ...flatFilter
        }
    } 
    else if ((typeof dataType[columnName] === 'object' && f.filterType !== 'date' && columnName in dataType && (!!(<CustomUIFilter>dataType[columnName])?.getConversionFilter))) {
        const xQuery = singleOrMultipleQuery(queryFilteredOrdered, dataType[columnName], f);

        if(xQuery) {
            queryFilteredOrdered['x_query'] = xQuery;
        }
    }
    return queryFilteredOrdered;
}

export const flattenObject = (obj: any, prefix = ''): Record<string, any> => {
    return Object.keys(obj).reduce((acc: any, k: string) => {
        const pre = prefix.length ? prefix + '.' : '';
        if (
            typeof obj[k] === 'object' && 
            obj[k] !== null && 
            !Array.isArray(obj[k]) && 
            !(obj[k] instanceof Date)
        ) {
            Object.assign(acc, flattenObject(obj[k], pre + k));
        } else {
            acc[pre + k] = obj[k];
        }
        return acc;
    }, {});
};

export const singleOrMultipleQuery = (queryFilteredOrdered: XQueryFilterOrder, columnValue: CustomUIFilter, filter: any) => {
    const conversionFilter = columnValue.getConversionFilter(filter);

    return queryFilteredOrdered['x_query']
        ? `AND(${queryFilteredOrdered['x_query']},${conversionFilter})`
        : conversionFilter;
}

export const customStringConvertionFilter = (params: TextFilterModel, x_queryPrefix: OperatorType): { prefix: ApiFilterPrefix, x_queryPrefix: OperatorType, inputFilter: string } => {
    let prefix: ApiFilterPrefix = '';
    let inputFilter = params.filter || '';
    switch (params.type) {
        case 'contains':
            prefix = '_like'
            break;
        case 'equals':
            break
        case 'blank':
            inputFilter = 'null';
            x_queryPrefix = 'AND';
            break;
        case 'notBlank':
            inputFilter = 'notnull';
            break
    }
    return {
        prefix,
        x_queryPrefix,
        inputFilter
    }
}

/**
 * Parses a value string into its correct JavaScript type.
 * @param {string} valueStr - The string to parse (e.g., '1', "'prova'", "true").
 * @returns {any} The parsed value (e.g., 1, 'prova', true).
 */
function parseValue(valueStr: string): any {
  const trimmedValue = valueStr.trim();

  if (trimmedValue === 'true') {
    return true;
  }
  if (trimmedValue === 'false') {
    return false;
  }

  if ((trimmedValue.startsWith("'") && trimmedValue.endsWith("'")) ||
      (trimmedValue.startsWith('"') && trimmedValue.endsWith('"'))) {
    return trimmedValue.slice(1, -1);
  }

  const numericValue = +trimmedValue;
  if (!Number.isNaN(numericValue) && trimmedValue.trim() !== '') {
    return numericValue;
  }

  return trimmedValue;
}

/**
 * Transforms an array of "key:value" strings into an object.
 * @param {string[]} arr - The input array.
 * @returns {object} The resulting object.
 */
export const transformArrayToObject = (arr: string[]): { [key: string]: any } => {
  return arr.reduce((accumulator, currentString) => {
    const separatorIndex = currentString.indexOf(':');

    if (separatorIndex === -1) {
      return accumulator;
    }

    const key = currentString.slice(0, separatorIndex).trim();
    const valueString = currentString.slice(separatorIndex + 1);

    accumulator[key] = parseValue(valueString);
    return accumulator;
  }, {} as { [key: string]: any });
}

export function getProperty<T, K extends keyof T>(obj: T, key: K): T[K] {
  return obj[key];
}